/**
 * Action Modal Block Types
 *
 * Defines the type system for level-specific action modal content blocks.
 * Each civic level can have varied content structures while maintaining
 * the same 4 modal headings: Look Deeper, One Key Insight, Explore Connections, Take It Further
 */

// Base block type
export interface BaseBlock {
  id?: string; // Optional for internal tracking in admin
}

// Rich text content block
export interface RichTextBlock extends BaseBlock {
  type: 'richText';
  title?: string;
  body: string;
}

// Bullet list block
export interface BulletsBlock extends BaseBlock {
  type: 'bullets';
  title?: string;
  items: Array<{ text: string }>;
}

// Reflection prompt block
export interface ReflectionPromptBlock extends BaseBlock {
  type: 'reflectionPrompt';
  title: string;
  prompt: string;
  placeholder?: string;
  minChars?: number;
  maxChars?: number;
}

// Insight card block
export interface InsightCardBlock extends BaseBlock {
  type: 'insightCard';
  title?: string;
  statement: string;
  supportingPoints?: string[];
}

// Compare grid block (side-by-side comparison)
export interface CompareGridBlock extends BaseBlock {
  type: 'compareGrid';
  title?: string;
  leftLabel: string;
  rightLabel: string;
  leftItems: string[];
  rightItems: string[];
}

// Timeline block
export interface TimelineBlock extends BaseBlock {
  type: 'timeline';
  title?: string;
  events: Array<{
    year: string;
    label: string;
    detail?: string;
  }>;
}

// Connections list block (related songs)
export interface ConnectionsListBlock extends BaseBlock {
  type: 'connectionsList';
  title?: string;
  items: Array<{
    songId?: string;
    title: string;
    artist: string;
    reason: string;
    tags?: string[];
  }>;
}

// Select one option (for Explorer level)
export interface SelectOneBlock extends BaseBlock {
  type: 'selectOne';
  title?: string;
  prompt: string;
  options: Array<{
    id: string;
    text: string;
  }>;
}

// World noticing prompt (for Explorer level)
export interface WorldNoticingPromptBlock extends BaseBlock {
  type: 'worldNoticingPrompt';
  title?: string;
  prompt: string;
  placeholder?: string;
}

// Insight chips block (for Explorer One Key Insight)
export interface InsightChipsBlock extends BaseBlock {
  type: 'insightChips';
  chips: Array<{
    id: string;
    text: string;
  }>;
}

// Compare prompt (for Sense-Maker level)
export interface ComparePromptBlock extends BaseBlock {
  type: 'comparePrompt';
  title?: string;
  prompt: string;
  leftLabel: string;
  rightLabel: string;
  placeholder?: string;
}

// Short write (for Sense-Maker level)
export interface ShortWriteBlock extends BaseBlock {
  type: 'shortWrite';
  title?: string;
  prompt: string;
  placeholder?: string;
  minChars?: number;
  maxChars?: number;
}

// Evidence prompt (for Sense-Maker level)
export interface EvidencePromptBlock extends BaseBlock {
  type: 'evidencePrompt';
  title?: string;
  prompt: string;
  placeholder?: string;
  requiresUrl?: boolean;
}

// Audience explainer prompt (for Advocate level)
export interface AudienceExplainerPromptBlock extends BaseBlock {
  type: 'audienceExplainerPrompt';
  title?: string;
  prompt: string;
  audienceOptions?: string[];
  placeholder?: string;
}

// Stakeholder map prompt (for Catalyst level)
export interface StakeholderMapPromptBlock extends BaseBlock {
  type: 'stakeholderMapPrompt';
  title?: string;
  prompt: string;
  categories?: string[];
  placeholder?: string;
}

// Pathway selector (for Take It Further)
export interface PathwaySelectorBlock extends BaseBlock {
  type: 'pathwaySelector';
  title?: string;
  description?: string;
  pathways: Array<{
    id: string;
    label: string;
    description: string;
    icon?: string;
  }>;
}

// Action cards block (for real-world actions - Catalyst+)
export interface ActionCardsBlock extends BaseBlock {
  type: 'actionCards';
  title?: string;
  items: Array<{
    title: string;
    orgName: string;
    region?: string;
    actionType: 'Learn' | 'Support';
    url?: string;
    whyThisOrg?: string;
    safetyNote?: string;
  }>;
}

// Contribution cards (for Change-Maker level)
export interface ContributionCardsBlock extends BaseBlock {
  type: 'contributionCards';
  title?: string;
  items: Array<{
    title: string;
    partnerName: string;
    region?: string;
    contributionType: 'Skills' | 'Time' | 'Resource' | 'Research';
    url?: string;
    expectations?: string;
    safetyNote?: string;
  }>;
}

// Checklist block
export interface ChecklistBlock extends BaseBlock {
  type: 'checklist';
  title?: string;
  items: Array<{ text: string }>;
}

// Plan builder block (multi-step planning)
export interface PlanBuilderBlock extends BaseBlock {
  type: 'planBuilder';
  title?: string;
  steps: Array<{
    title: string;
    placeholder?: string;
  }>;
}

// Share template block
export interface ShareTemplateBlock extends BaseBlock {
  type: 'shareTemplate';
  title?: string;
  templates: Array<{
    label: string;
    templateText: string;
  }>;
}

// Partner links block
export interface PartnerLinksBlock extends BaseBlock {
  type: 'partnerLinks';
  title?: string;
  items: Array<{
    name: string;
    region?: string;
    url: string;
    description: string;
    trustBadge?: string;
  }>;
}

// Placeholder block (for missing content)
export interface PlaceholderBlock extends BaseBlock {
  type: 'placeholder';
  title: string;
  body: string;
}

// Union type of all blocks
export type ActionModalBlock =
  | RichTextBlock
  | BulletsBlock
  | ReflectionPromptBlock
  | InsightCardBlock
  | CompareGridBlock
  | TimelineBlock
  | ConnectionsListBlock
  | SelectOneBlock
  | WorldNoticingPromptBlock
  | InsightChipsBlock
  | ComparePromptBlock
  | ShortWriteBlock
  | EvidencePromptBlock
  | AudienceExplainerPromptBlock
  | StakeholderMapPromptBlock
  | PathwaySelectorBlock
  | ActionCardsBlock
  | ContributionCardsBlock
  | ChecklistBlock
  | PlanBuilderBlock
  | ShareTemplateBlock
  | PartnerLinksBlock
  | PlaceholderBlock;

// Section content (one of the 4 headings)
export interface ActionModalSection {
  blocks: ActionModalBlock[];
}

// Level content (all 4 sections)
export interface ActionModalLevelContent {
  lookDeeper: ActionModalSection;
  oneKeyInsight: ActionModalSection;
  exploreConnections: ActionModalSection;
  takeItFurther: ActionModalSection;
}

// Full structure for all levels
export interface ActionModalsByLevel {
  observer?: ActionModalLevelContent;
  explorer?: ActionModalLevelContent;
  senseMaker?: ActionModalLevelContent;
  advocate?: ActionModalLevelContent;
  catalyst?: ActionModalLevelContent;
  changeMaker?: ActionModalLevelContent;
}

// Type for section keys
export type ActionModalSectionKey = 'lookDeeper' | 'oneKeyInsight' | 'exploreConnections' | 'takeItFurther';

// Type for level keys
export type ActionModalLevelKey = 'observer' | 'explorer' | 'senseMaker' | 'advocate' | 'catalyst' | 'changeMaker';

// Helper to map civic level names to keys
export function civicLevelToKey(level: string): ActionModalLevelKey {
  const mapping: Record<string, ActionModalLevelKey> = {
    'Observer': 'observer',
    'Explorer': 'explorer',
    'Sense-Maker': 'senseMaker',
    'Advocate': 'advocate',
    'Catalyst': 'catalyst',
    'Change-Maker': 'changeMaker',
  };
  return mapping[level] || 'observer';
}

// Helper to get section key from modal type
export function modalTypeToSectionKey(modalType: string): ActionModalSectionKey {
  const mapping: Record<string, ActionModalSectionKey> = {
    'look-deeper': 'lookDeeper',
    'look_deeper': 'lookDeeper',
    'one-key-insight': 'oneKeyInsight',
    'one_key_insight': 'oneKeyInsight',
    'explore-connections': 'exploreConnections',
    'explore_connections': 'exploreConnections',
    'take-it-further': 'takeItFurther',
    'take_it_further': 'takeItFurther',
  };
  return mapping[modalType] || 'lookDeeper';
}
